import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { differenceInSeconds } from "date-fns";

import {
  SessionDataRequestBody,
  useGetHealthQuery,
  usePostSessionDataMutation,
} from "../redux/services/config";
import { setOnlineMode } from "../redux/slices/applicationSlice";

import { getFailedSessionRequests, setFailedSessionRequests } from "../utils";

export const useOnlineMode = (sendOfflineEvent: () => void) => {
  const dispatch = useDispatch();
  const lastOnlineStatus = useRef<boolean | null>(null);

  const { fulfilledTimeStamp, startedTimeStamp, refetch, isFetching, isError } =
    useGetHealthQuery();

  const [postSessionData] = usePostSessionDataMutation();

  const resendFailedSessionRequests = useCallback(() => {
    const pastFailedRequests = getFailedSessionRequests();
    const failedRequests: SessionDataRequestBody[] = [];

    if (pastFailedRequests.length) {
      pastFailedRequests.forEach((session: SessionDataRequestBody) => {
        postSessionData(session)
          .unwrap()
          .catch((error) => {
            if (error.status !== 400) failedRequests.push(session);
          });
      });

      setFailedSessionRequests(failedRequests);
    }
  }, [postSessionData]);

  const logConnectivityChange = useCallback((isOnline: boolean) => {
    console.info(
      `[useOnlineMode] Connectivity status: ${isOnline ? "online" : "offline"}`,
    );
  }, []);

  const handleOnline = useCallback(() => {
    if (lastOnlineStatus.current !== true) {
      logConnectivityChange(true);
    }
    lastOnlineStatus.current = true;
    dispatch(setOnlineMode(true));
    resendFailedSessionRequests();
  }, [dispatch, logConnectivityChange, resendFailedSessionRequests]);

  const handleOffline = useCallback(() => {
    if (lastOnlineStatus.current !== false) {
      logConnectivityChange(false);
      sendOfflineEvent();
    }
    lastOnlineStatus.current = false;
    dispatch(setOnlineMode(false));
  }, [dispatch, logConnectivityChange, sendOfflineEvent]);

  useEffect(() => {
    if (isError) {
      handleOffline();
      return;
    }

    if (fulfilledTimeStamp && startedTimeStamp && !isFetching) {
      const waitTime = differenceInSeconds(
        new Date(fulfilledTimeStamp),
        new Date(startedTimeStamp),
      );

      if (waitTime < 10) {
        handleOnline();
      } else {
        handleOffline();
      }
    }
  }, [
    fulfilledTimeStamp,
    handleOffline,
    handleOnline,
    isError,
    isFetching,
    startedTimeStamp,
  ]);

  return { checkOnlineMode: refetch };
};
